/**
 * ActionButtons Component
 * 
 * A reusable component that provides consistent action buttons for admin forms.
 * Used across Events and Bulletins create/edit screens.
 * 
 * Features:
 * - Cancel, Submit, and optional Delete buttons
 * - Loading states with spinner animation
 * - Configurable button text
 * - Consistent styling and positioning (fixed at bottom)
 * - Disabled state handling during loading
 * 
 * Usage:
 * ```tsx
 * <ActionButtons
 *   isLoading={isSubmitting}
 *   onCancel={() => navigation.goBack()}
 *   onSubmit={handleSubmit}
 *   onDelete={handleDelete}
 *   submitText="Create Event"
 *   showDelete={true}
 * />
 * ```
 */
import React, { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';

interface ActionButtonsProps {
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onDelete?: () => void;
  submitText?: string;
  showDelete?: boolean;
  cancelText?: string;
  deleteText?: string;
}

export const ActionButtons = memo<ActionButtonsProps>(({
  isLoading,
  onCancel,
  onSubmit,
  onDelete,
  submitText = 'Submit',
  showDelete = false,
  cancelText = 'Cancel',
  deleteText = 'Delete',
}) => {
  return (
    <View style={styles.actionButtons}>
      {showDelete && onDelete && (
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={onDelete}
          disabled={isLoading}
          activeOpacity={0.7}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={styles.deleteButtonText}>{deleteText}</Text>
        </TouchableOpacity>
      )}
      
      <TouchableOpacity
        style={styles.cancelButton}
        onPress={onCancel}
        disabled={isLoading}
        activeOpacity={0.7}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Text style={styles.cancelButtonText}>{cancelText}</Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.submitButton, isLoading && styles.submitButtonDisabled]}
        onPress={onSubmit}
        disabled={isLoading}
        activeOpacity={0.7}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        {isLoading ? (
          <ActivityIndicator size="small" color="#FFFFFF" />
        ) : (
          <Text style={styles.submitButtonText}>{submitText}</Text>
        )}
      </TouchableOpacity>
    </View>
  );
});

ActionButtons.displayName = 'ActionButtons';

const styles = StyleSheet.create({
  actionButtons: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    padding: 16,
    flexDirection: 'row',
    gap: 12,
    zIndex: 100, // Ensure buttons appear above other content
  },
  deleteButton: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.ERROR,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  deleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  submitButton: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
